// frontend/src/pages/PlaylistPage.js
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { ArrowLeft, PlayCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import apiService from "../services/apiService";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ContractBadge from "../components/common/ContractBadge";
import LanguageBadge from "../components/common/LanguageBadge";
import ShareButton from "../components/common/ShareButton";
import PlaylistItemCard from "../components/common/PlaylistItemCard";
import useSocialMeta from "../hooks/useSocialMeta";


const PlaylistPage = () => {
  const { slug } = useParams();
  const [playlistData, setPlaylistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t } = useLanguage();
  const { hasAccess, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Social media meta tags
  useSocialMeta({
    title: playlistData ? t(playlistData.playlist.title) : null,
    description: playlistData ? t(playlistData.playlist.description) || 'Watch this playlist on burnair Academy' : null,
    image: playlistData ? (playlistData.playlist.imageUrl || playlistData.playlist.thumbnailUrl) : null,
    url: `/playlist/${slug}`,
    type: 'video'
  });

  useEffect(() => {
    loadPlaylist();
  }, [slug]);

  const handleLoginRequired = () => {
    // Pass both state and query param for return URL
    const currentPath = location.pathname;
    navigate(`/login?returnUrl=${encodeURIComponent(currentPath)}`, {
      state: { from: currentPath }
    });
  };

  const handleBack = () => {
    // Check if we came from a category
    if (location.state?.categorySlug) {
      navigate(`/category/${location.state.categorySlug}`);
    } else {
      navigate(-1);
    }
  };

  const playFirstVideo = () => {
    if (!playlistData?.items) return;
    
    // Find the first video item that user has access to
    const firstVideo = playlistData.items.find(
      item => item.type === "video" && hasAccess(item.contract)
    );
    
    if (firstVideo) {
      const index = playlistData.items.indexOf(firstVideo);
      navigate(`/video/${firstVideo.slug}`, {
        state: { 
          playlist: playlistData.items, 
          currentIndex: index,
          playlistSlug: slug,
          categorySlug: location.state?.categorySlug
        }
      });
    }
  };

  const loadPlaylist = async () => {
    try {
      const response = await apiService.getPlaylistBySlug(slug);
      if (response.success) {
        setPlaylistData(response.data);
      } else {
        setError(response.message);
      }
    } catch (error) {
      console.error("Error loading playlist:", error);
      setError("Failed to load playlist");
    } finally {
      setLoading(false);
    }
  };


  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={handleBack}
          className="flex items-center text-[#00A3E0] hover:text-[#002855] mb-4 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          {t({ en: "Back", de: "Zurück" })}
        </button>
        <div className="text-center text-[#DA291C]">Error: {error}</div>
      </div>
    );
  }

  return (
    <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-[#00A3E0] hover:text-[#002855] mb-4 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          {t({ en: "Back", de: "Zurück" })}
        </button>

        <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
          {/* Show access denied UI if user doesn't have access */}
          {playlistData.playlist.hasAccess === false ? (
            <div className="relative aspect-video overflow-hidden">
              {/* Background with poster - no blur for better teaser */}
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: `url(${playlistData.playlist.thumbnailUrl || playlistData.playlist.imageUrl})`,
                }}
              />
              <div className="absolute inset-0 bg-black bg-opacity-60" />
              
              {/* Content overlay */}
              <div className="relative h-full flex items-center justify-center p-8">
                <div className="text-center max-w-lg">
                  {/* Lock icon */}
                  <div className="mx-auto w-20 h-20 mb-6 rounded-full bg-white bg-opacity-10 flex items-center justify-center">
                    <svg
                      className="w-10 h-10 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>
                  
                  {/* Access message */}
                  <div className="bg-white bg-opacity-10 rounded-lg p-4 mb-6">
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {t({ en: "Access Required", de: "Zugang erforderlich" })}
                    </h3>
                    <p className="text-gray-200">
                      {playlistData.playlist.contract === "Registered"
                        ? t({
                            en: "Sign in to watch this playlist",
                            de: "Melden Sie sich an, um diese Wiedergabeliste anzusehen",
                          })
                        : t({
                            en: `This content requires a burnair Map ${playlistData.playlist.contract} subscription`,
                            de: `Dieser Inhalt erfordert ein burnair Map ${playlistData.playlist.contract} Abonnement`,
                          })}
                    </p>
                  </div>
                  
                  {/* Action buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    {!isAuthenticated ? (
                      <button
                        onClick={handleLoginRequired}
                        className="bg-[#002855] text-white px-6 py-2 rounded-md hover:bg-[#001a3d] transition-colors"
                      >
                        {t({ en: "Login", de: "Anmelden" })}
                      </button>
                    ) : (
                      <a
                        href="https://www.burnair.cloud/upgrade"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-[#00A3E0] text-white px-8 py-3 rounded-md hover:bg-[#0090c7] transition-colors font-semibold"
                      >
                        {t({
                          en: "Upgrade Subscription",
                          de: "Abonnement erweitern",
                        })}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="aspect-video bg-gray-200 relative group">
              <img
                src={playlistData.playlist.thumbnailUrl}
                alt={t(playlistData.playlist.title)}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              
              {/* Play button overlay */}
              {playlistData.items.some(item => item.type === "video" && hasAccess(item.contract)) && (
                <button
                  onClick={playFirstVideo}
                  className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200"
                >
                  <PlayCircle className="w-20 h-20 text-white opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all duration-200 drop-shadow-lg" />
                </button>
              )}
              
              <div className="absolute top-4 right-4">
                <ContractBadge contract={playlistData.playlist.contract} />
              </div>
            </div>
          )}

          <div className="p-6">
            <div className="flex items-start justify-between mb-2">
              <h1 className="text-2xl font-bold text-[#002855]">
                {t(playlistData.playlist.title)}
              </h1>
              <ShareButton 
                url={`/playlist/${slug}`}
                title={t(playlistData.playlist.title)}
                description={t(playlistData.playlist.description) || 'Watch this playlist on burnair Academy'}
                variant="icon"
                className="flex items-center"
              />
            </div>
            {playlistData.playlist.description && (
              <p className="text-gray-600 leading-relaxed">
                {t(playlistData.playlist.description)}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Only show playlist contents if user has access */}
      {playlistData.playlist.hasAccess !== false && (
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h2 className="text-xl font-semibold text-[#002855] mb-4">
            {t({ en: "Playlist Contents", de: "Wiedergabelisten-Inhalt" })}
          </h2>

          <div className="space-y-3">
            {playlistData.items.map((item, index) => {
            // Calculate video number (excluding dividers)
            const videoNumber = item.type === 'video' 
              ? playlistData.items.slice(0, index).filter(i => i.type === 'video').length + 1
              : -1; // -1 for non-videos
            
            const handleItemClick = (clickedItem, idx) => {
              if (clickedItem.type === "video") {
                navigate(`/video/${clickedItem.slug}`, {
                  state: {
                    playlist: playlistData.items,
                    currentIndex: idx,
                    playlistSlug: slug,
                    categorySlug: location.state?.categorySlug
                  }
                });
              } else if (clickedItem.type === "playlist") {
                navigate(`/playlist/${clickedItem.slug}`, {
                  state: { categorySlug: location.state?.categorySlug }
                });
              } else if (clickedItem.type === "quiz") {
                navigate(`/quiz/${clickedItem.slug}`, {
                  state: { from: `/playlist/${slug}` }
                });
              }
            };
            
            return (
              <PlaylistItemCard
                key={`${item.type}-${item.id}-${index}`}
                item={item}
                index={index}
                videoNumber={videoNumber}
                isCurrentVideo={false}
                onItemClick={handleItemClick}
                shareUrl={item.type === "video" ? `/playlist/${slug}/video/${item.slug}` : null}
                showShareButton={item.type === "video"}
                variant="large"
              />
            );
          })}
        </div>

        {playlistData.items.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">
              {t({
                en: "This playlist is empty.",
                de: "Diese Wiedergabeliste ist leer.",
              })}
            </p>
          </div>
        )}
        </div>
      )}
    </main>
  );
};

export default PlaylistPage;
